import * as React from "react"
import { Col, Container, Row, Visible } from "react-grid-system"
import SearchField from "../../atoms/inputs/SearchField"
import Dropdown, { optionsMap } from "../../atoms/inputs/Dropdown"
import { useTranslation } from "react-i18next"
import ListIcon from "../../atoms/icons/ListIcon"
import { HorizontalSpacer, Spacer } from "../../atoms/spacers"
import DetailsIcon from "../../atoms/icons/DetailsIcon"
import { theme } from "../../theme"
import Subtitle from "../../atoms/typography/Subtitle"
import PrimaryButton from "../../atoms/buttons/PrimaryButton"
import IconButton from "../../atoms/buttons/IconButton"
import { formatGroups } from "../../../utils/formatters"
import { SettingsIcon } from "../../atoms/icons/SettingsIcon"
import { useAllGroups } from "../../../hooks/useAllGroups"
import { PAGINATION_VALUE, PAGINATION_VALUE_PRESETS } from "../../../utils/design-helpers"
import { useNavigate, useParams } from "react-router-dom"
import styled from "styled-components"
import PaginationComponent from "../../molecules/PaginationComponent"
import SecondaryButton from "../../atoms/buttons/SecondaryButton"
import Card from "../../atoms/Card"
import CardBar from "../../atoms/CardBar"
import { AddIcon } from "../../atoms/icons/AddIcon"
import { SlideOutHeight } from "../../atoms/animations/SlideOutHeight"
import CreateGroupOrganism from "../../organisms/CreateGroupOrganism"
import { GroupModel } from "../../../utils/models"
import GroupOverview from "../../organisms/GroupOverview"
import { useMyUser } from "../../../hooks/useMyUser"
import H4 from "../../atoms/typography/H4"
import Body from "../../atoms/typography/Body"
import { Key } from "react-aria-components"
import Button from "../../atoms/buttons/Button"

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 2rem;
`

const DROPDOWN_OPTIONS = [
  { id: "name", value: "name", name: "Name A-Z" },
  { id: "-name", value: "-name", name: "Name Z-A" },
  { id: "-members", value: "-members", name: "Members high - low" },
  { id: "members", value: "members", name: "Members low - high" },
]
const GroupsPage: React.FunctionComponent = () => {
  const { t } = useTranslation()
  const { page } = useParams()
  const navigate = useNavigate()
  const { user } = useMyUser()

  const userIsAdmin = user?.organizationRole?.name === "Admin"

  const [currentPage, setCurrentPage] = React.useState<number>(parseInt(page ?? "1"))
  const [offset, setoffset] = React.useState<number>(currentPage > 1 ? (currentPage - 1) * PAGINATION_VALUE : 0)

  const [searchValue, setSearchValue] = React.useState<undefined | string>(undefined)
  const [isEditing, setIsEditing] = React.useState(false)
  const [sortingId, setSortingId] = React.useState<Key>("name")
  const [createGroupOpen, setCreateGroupOpen] = React.useState(false)
  const [cardsOpen, setCardsOpen] = React.useState<Array<string>>([])

  const onSearch = (value: any) => {
    setSearchValue(value)
  }

  const sorting = optionsMap(DROPDOWN_OPTIONS)[sortingId].value!

  const { allGroups } = useAllGroups(PAGINATION_VALUE, offset, searchValue, sorting)

  const formattedGroups = formatGroups(allGroups?.edges ?? [])

  //Handle open sections
  const checkCardIsOpen = (id: string) => {
    return cardsOpen.includes(id)
  }
  const onClickCloseCard = (id: string) => {
    if (checkCardIsOpen(id)) {
      const arr = cardsOpen.filter((x) => x !== id)
      setCardsOpen(arr)
    } else {
      setCardsOpen([...cardsOpen, id])
    }
  }
  const onClickListView = () => {
    setCardsOpen([])
  }
  const allGroupsIds: Array<string> = []
  allGroups?.edges?.map((g: any) => allGroupsIds.push(g?.id))
  const onClickDetailsView = () => {
    setCardsOpen(allGroupsIds)
  }

  //Handle pagination
  const totalCount = allGroups?.totalCount ?? 1
  const pagesInitialValue = Math.ceil(totalCount / PAGINATION_VALUE)
  const totalPages = totalCount < PAGINATION_VALUE ? 1 : pagesInitialValue
  const onClickPage = (newPage: number) => {
    if (newPage !== currentPage) {
      setoffset(offset ? PAGINATION_VALUE * (newPage - 1) : PAGINATION_VALUE)
      setCurrentPage(newPage)
      navigate(`/organization/groups/${newPage}`)
    }
  }

  return (
    <div>
      <Row align="end">
        <Col lg={2} md={12}>
          <SearchField onChange={(e) => onSearch(e)} />
          <Visible md xs>
            <HorizontalSpacer spacing={2} />
          </Visible>
        </Col>
        <Col lg={2} md={7}>
          <Dropdown
            label="Sort by"
            options={DROPDOWN_OPTIONS}
            onSelectionChange={(id) => setSortingId(id)}
            selectedKey={sortingId}
          />
        </Col>
        <Col lg={2} md={5}>
          <div style={{ display: "flex" }}>
            <Button
              size="normal"
              iconLeft={<ListIcon small color={cardsOpen.length < 1 ? theme.colors.white : theme.colors.greyDark} />}
              onPress={onClickListView}
              variant={cardsOpen.length < 1 ? "dark" : "light"}
            />
            <Spacer spacing={1} />
            <Button
              size="normal"
              onPress={onClickDetailsView}
              variant={cardsOpen.length === allGroupsIds.length ? "dark" : "light"}
              iconLeft={
                <DetailsIcon
                  small
                  color={cardsOpen.length === allGroupsIds.length ? theme.colors.white : theme.colors.greyDark}
                />
              }
            />
          </div>
        </Col>
      </Row>
      <HorizontalSpacer spacing={2} />
      <Row justify="between" style={{ padding: "0 15px" }}>
        <Subtitle>{t("groups")}</Subtitle>
        {userIsAdmin && !isEditing && (
          <PrimaryButton title="Manage groups" icon={<SettingsIcon />} onClick={() => setIsEditing(true)} />
        )}
        {isEditing && <SecondaryButton title="Done" onClick={() => setIsEditing(false)} />}
      </Row>
      <HorizontalSpacer spacing={2} />
      {isEditing && (
        <>
          <Card noPadding>
            <CardBar open={createGroupOpen}>
              <Row
                justify="center"
                onClick={() => setCreateGroupOpen(!createGroupOpen)}
                align="center"
                style={{ cursor: "pointer" }}
              >
                <AddIcon color={theme.colors.white} />
                <Spacer spacing={1} />
                <Subtitle color={theme.colors.white}>Create new group</Subtitle>
              </Row>
            </CardBar>
            <SlideOutHeight isVisible={createGroupOpen}>
              <CreateGroupOrganism onClickCancelEditing={() => setCreateGroupOpen(false)} />
            </SlideOutHeight>
          </Card>
          <HorizontalSpacer spacing={3} />
        </>
      )}
      {formattedGroups.map((g: GroupModel, index: number) => (
        <div key={`${g?.id} ${index}`}>
          <GroupOverview
            members={g.members}
            groupName={g.groupName}
            open={checkCardIsOpen(g?.id)}
            onClickClose={() => onClickCloseCard(g?.id)}
            terminals={g.terminals}
            isEditing={isEditing}
            groupId={g?.id}
            orgId={g?.organization.id}
            isAdminGroup={g.isAdminGroup}
          />
          <HorizontalSpacer spacing={3} />
        </div>
      ))}

      <PaginationContainer>
        <PaginationComponent
          itemsPerPageValues={PAGINATION_VALUE_PRESETS}
          pages={totalPages}
          currentPageIndex={currentPage}
          onClickPage={onClickPage}
        />
      </PaginationContainer>

      <Container>
        <HorizontalSpacer spacing={3} />
        <H4>What are Groups? </H4>
        <HorizontalSpacer spacing={1} />
        <Body>
          The Groups feature enables your organization to manage who has access to which terminals by assigning members
          and terminals to specific Groups. Using this feature, you can easily keep track of, and manage terminal
          accesses for all parts of your Organization and monitor each group's data usage. Both terminals and members
          can be assigned to multiple groups to meet your Organization's needs. Members can be assigned different roles
          in different groups, to match their roles within the organization. Members can only view the groups they are
          permitted to, ensuring that the right members have access to the right information.
        </Body>
        <HorizontalSpacer spacing={1} />
        <H4>Should I create a Group or a Sub-organization?</H4>
        <HorizontalSpacer spacing={1} />
        <Body>
          Groups are best used within your organization to keep track of who has access to what, and which terminals are
          being used for which projects. Groups are also useful for shorter projects and enable a clear oversight within
          your organization.
          <HorizontalSpacer spacing={1} />
          Sub-organizations are separate organizations which can be viewed and accessed by authorized personnel from the
          main organization. Sub-organizations can make purchases and have groups of their own and are useful for
          subsidiaries and independently operating parts of your organization.
          <HorizontalSpacer spacing={1} />
          Terminals can be shared between groups, but not between organizations and sub-organizations.
        </Body>
        <HorizontalSpacer spacing={3} />
      </Container>
    </div>
  )
}

export default GroupsPage
